import { useEffect, useRef } from 'react';
import { cameraReleaseUrl } from '../../lib/config';

function appendQuery(url: string, params: Record<string, string | null | undefined>): string {
  const entries = Object.entries(params).filter((entry): entry is [string, string] => Boolean(entry[1]));
  if (entries.length === 0) return url;
  const query = entries.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`).join('&');
  return `${url}${url.includes('?') ? '&' : '?'}${query}`;
}

function cameraIdFromFeedUrl(feedUrl: string): string {
  const parts = feedUrl.split('/camera/feed/').pop()?.split('/') ?? [];
  return parts[parts.length - 1].split('?')[0];
}

function makeSessionId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
      });
}

/** Find the JPEG marker `ff` `second` in buf at/after `from`, or -1. */
function findMarker(buf: Uint8Array, second: number, from: number): number {
  for (let i = from; i < buf.length - 1; i++) {
    if (buf[i] === 0xff && buf[i + 1] === second) return i;
  }
  return -1;
}

const RETRY_MIN_MS = 1000;
const RETRY_MAX_MS = 15000;
// A stuck parse buffer this large means the stream is not MJPEG — drop it.
const MAX_BUFFER_BYTES = 8 * 1024 * 1024;

/**
 * MJPEG viewer that reads the multipart stream itself (fetch + ReadableStream)
 * and paints frames into an <img> via blob URLs.
 *
 * A plain <img src=mjpeg> gives no reliable end-of-stream signal, so when the
 * backend ends a starved stream (dead camera) the tile would just freeze while
 * holding a socket. Safari also aborts fetch() on `multipart/x-mixed-replace`,
 * so the backend serves `application/octet-stream` and the multipart framing
 * only exists as bytes we skip over. Reading the stream directly makes the end explicit
 * (reader done / fetch error) and lets us reconnect with exponential backoff.
 * Frames are delimited by JPEG SOI/EOI markers — the encoder (cv2.imencode)
 * emits no nested thumbnails, so a bare ffd8/ffd9 scan is unambiguous.
 */
export function MjpegImg({ src, alt, className }: { src: string; alt?: string; className?: string }) {
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    // Capture the node now: on unmount React nulls imgRef.current during the
    // commit phase, BEFORE this passive cleanup runs.
    const node = imgRef.current;
    if (!node) return;

    const cameraId = cameraIdFromFeedUrl(src);
    let disposed = false;
    let controller: AbortController | null = null;
    let retryTimer: number | undefined;
    let retryMs = RETRY_MIN_MS;
    let blobUrl: string | null = null;

    const paint = (jpeg: Uint8Array<ArrayBuffer>) => {
      const url = URL.createObjectURL(new Blob([jpeg], { type: 'image/jpeg' }));
      node.src = url;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      blobUrl = url;
    };

    const connect = async () => {
      const sessionId = makeSessionId();
      const releaseUrl = cameraReleaseUrl(cameraId, sessionId);
      controller = new AbortController();
      try {
        const res = await fetch(appendQuery(src, { session: sessionId }), {
          signal: controller.signal,
          cache: 'no-store',
        });
        if (!res.ok || !res.body) throw new Error(`camera feed HTTP ${res.status}`);
        const reader = res.body.getReader();
        let buf: Uint8Array<ArrayBuffer> = new Uint8Array(0);
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          const merged = new Uint8Array(buf.length + value.length);
          merged.set(buf);
          merged.set(value, buf.length);
          buf = merged;
          for (;;) {
            const soi = findMarker(buf, 0xd8, 0);
            if (soi < 0) {
              if (buf.length > MAX_BUFFER_BYTES) buf = new Uint8Array(0);
              break;
            }
            const eoi = findMarker(buf, 0xd9, soi + 2);
            if (eoi < 0) {
              if (buf.length - soi > MAX_BUFFER_BYTES) buf = new Uint8Array(0);
              else if (soi > 0) buf = buf.slice(soi);
              break;
            }
            paint(buf.slice(soi, eoi + 2));
            retryMs = RETRY_MIN_MS;
            buf = buf.slice(eoi + 2);
          }
        }
      } catch {
        // Aborted on unmount, or network/server failure — both fall through to
        // the release + retry path below.
      } finally {
        // Best-effort server-side viewer release; idempotent if the backend
        // already reaped the session when it ended the stream.
        navigator.sendBeacon(releaseUrl);
      }
      if (disposed) return;
      retryTimer = window.setTimeout(connect, retryMs);
      retryMs = Math.min(retryMs * 2, RETRY_MAX_MS);
    };

    void connect();

    return () => {
      disposed = true;
      controller?.abort();
      window.clearTimeout(retryTimer);
      node.removeAttribute('src');
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [src]);

  return <img ref={imgRef} alt={alt} className={className} />;
}
